import { object, string, boolean, SchemaOf } from 'yup'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import {
  citizenSupportingDocumentNotRequired,
  nonCitizenSupportingDocumentNotRequired,
} from '@utils/documentCheckCriteria'
import { countriesWithSubdivisions } from '@utils/countries'

export interface DocumentCheckValues extends Enrollment.PreDocumentCheckStep {
  photoId: string
  document?: string
  namesOnDocumentsMatch?: boolean
  citizenshipChanged?: boolean
  membershipNameMatchesLegalName?: boolean
  citizenshipCountry: string
  nonImmigrantVisaStatus: string
  birthCountry: string
  birthSubdivision: string
  birthLocality: string
}

export enum DocumentCheckKeys {
  PHOTO_ID = 'photoId',
  DOCUMENT = 'document',
  NAME_ON_DOCUMENTS_MATCH = 'namesOnDocumentsMatch',
  COUNTRY_OF_BIRTH = 'birthCountry',
  CITY_OF_BIRTH = 'birthLocality',
  STATE_OF_BIRTH = 'birthSubdivision',
  CITIZENSHIP_COUNTRY = 'citizenshipCountry',
  NON_IMMIGRANT_VISA_STATUS = 'nonImmigrantVisaStatus',
}

export const DocumentCheckLabels = {
  [DocumentCheckKeys.PHOTO_ID]: 'Choose a Photo ID',
  [DocumentCheckKeys.DOCUMENT]: 'Citizenship/Immigration Document',
  [DocumentCheckKeys.COUNTRY_OF_BIRTH]: 'Country of Birth',
  [DocumentCheckKeys.CITY_OF_BIRTH]: 'City of Birth',
  [DocumentCheckKeys.STATE_OF_BIRTH]: 'State/Province of Birth',
  [DocumentCheckKeys.CITIZENSHIP_COUNTRY]: 'Country of Citizenship',
  [DocumentCheckKeys.NON_IMMIGRANT_VISA_STATUS]:
    'Are you a Lawful Permanent Resident (LPR)?',
}

const isNameLinkingDocumentRequired = (photoId: string) => {
  const supportingDocumentRequired = !(
    citizenSupportingDocumentNotRequired.includes(photoId) ||
    nonCitizenSupportingDocumentNotRequired.includes(photoId)
  )
  return supportingDocumentRequired
}

export const documentCheckSchema: SchemaOf<DocumentCheckValues> =
  object().shape({
    photoId: string().required(
      'Please select either a List A or List B document'
    ),
    document: string().when('photoId', {
      is: isNameLinkingDocumentRequired,
      then: string().required('Citizenship/Immigration Document is required'),
      otherwise: string().notRequired(),
    }),
    namesOnDocumentsMatch: boolean().when('photoId', {
      is: isNameLinkingDocumentRequired,
      then: boolean().required(),
      otherwise: boolean().notRequired(),
    }),
    citizenshipChanged: boolean(),
    membershipNameMatchesLegalName: boolean(),
    [DocumentCheckKeys.COUNTRY_OF_BIRTH]: string()
      .required()
      .label(DocumentCheckLabels[DocumentCheckKeys.COUNTRY_OF_BIRTH]),
    [DocumentCheckKeys.CITY_OF_BIRTH]: string()
      .required()
      .label(DocumentCheckLabels[DocumentCheckKeys.CITY_OF_BIRTH]),
    [DocumentCheckKeys.STATE_OF_BIRTH]: string()
      .label(DocumentCheckLabels[DocumentCheckKeys.STATE_OF_BIRTH])
      .when(DocumentCheckKeys.COUNTRY_OF_BIRTH, {
        is: (value: string) => countriesWithSubdivisions.includes(value),
        then: string().required(),
      }),
    [DocumentCheckKeys.CITIZENSHIP_COUNTRY]: string()
      .required()
      .label(DocumentCheckLabels[DocumentCheckKeys.CITIZENSHIP_COUNTRY]),
    [DocumentCheckKeys.NON_IMMIGRANT_VISA_STATUS]: string()
      .oneOf(['lpr'])
      .label(DocumentCheckLabels[DocumentCheckKeys.NON_IMMIGRANT_VISA_STATUS])
      .when(DocumentCheckKeys.CITIZENSHIP_COUNTRY, {
        is: (value?: string) => value !== 'US' && !!value,
        then: string().required(),
      }),
  })
